/**
 * End-of-show behaviour, as configured per lightshow. MM #178.
 *
 * The schedule payload carries `endConfig` straight from the script:
 *
 *   { redirect: 'pipe' | 'url' | 'none', redirectUrl?: string, text?: string }
 *
 * Absent = the pre-#178 behaviour (back to the pipe, translated thanks text),
 * so a show prepared before this shipped is unaffected.
 *
 * This mirrors `app/imports/api/lightshow/utils/endConfig.js`, which is the
 * source of truth and where the values are validated on write. The lightshow
 * is a standalone SPA with no access to the Meteor tree, so the read-side
 * resolution is deliberately duplicated here — keep the two in step.
 */

/** Only http(s) may reach `window.location.href`. */
const SAFE_URL = /^https?:\/\/[^\s]+$/i;

/**
 * Where to send the phone once the show ends.
 *
 * @param {object|null|undefined} endConfig from the schedule payload
 * @param {string} pipeUrl the show's own pipe — the default target
 * @returns {string|null} absolute URL, or null to stay on the closing screen
 */
export function resolveEndRedirect(endConfig, pipeUrl) {
  const redirect = endConfig?.redirect ?? 'pipe';

  if (redirect === 'none') return null;
  if (redirect === 'url') {
    const url = typeof endConfig?.redirectUrl === 'string' ? endConfig.redirectUrl.trim() : '';
    // A broken target must not strand the audience on a dead screen.
    return SAFE_URL.test(url) ? url : pipeUrl;
  }
  return pipeUrl;
}

/**
 * Whether the phone will navigate away at all.
 *
 * Deliberately NOT expressed as `resolveEndRedirect(cfg, null) === null`: that
 * conflates "configured to stay" with "no pipe URL supplied", and pipe mode
 * would then also read as staying.
 *
 * @param {object|null|undefined} endConfig
 * @returns {boolean}
 */
export function willRedirect(endConfig) {
  return (endConfig?.redirect ?? 'pipe') !== 'none';
}

/**
 * The closing line to render.
 *
 * @param {object|null|undefined} endConfig from the schedule payload
 * @param {string} fallback the translated default
 * @returns {string}
 */
export function resolveEndText(endConfig, fallback) {
  const text = typeof endConfig?.text === 'string' ? endConfig.text.trim() : '';
  return text || fallback;
}
